import React, { useContext, useEffect, useRef, useState } from 'react';
import AudioPlayer from 'react-h5-audio-player';
import { Box, Stack, Link as MuiLink } from '@mui/material';
import { useMusicStore } from '../store/store';
import { Link } from 'react-router-dom';
import { getAlbumThumbnailWithToken, getCompilationThumbnailWithToken, postCompilationIncrementTrackListenCount, postIncrementTrackListenCount } from '../apis/music-api';
import { FileResponse } from '../../types/FileResponse';
import { AuthenticationContext } from '../authentication/AuthenticationContext';
import { format } from '../text-helper/text-helper';
import { postNowPlaying, postScrobble } from '../apis/last-fm-api';
import callApi from '../call-api/call-api';
import lastFmLogo from '../../assets/lastfm-logo.png';
import { PlayModes } from '../../types/PlayModes';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import LyricsModal from '../../modals/lyrics-modal/lyrics-modal';
import { useLyricsModal } from '../../hooks/useLyricsModal';

const MusicPlayer = () => {
    const { isAuthenticated, getMusicToken, getAccessToken } = useContext(AuthenticationContext);
    const {
        playlist,
        trackIndex,
        setTrackIndex,
        currentTrack,
        setCurrentTrack,
        setIsPlaying,
        playMode,
        setArtistName,
    } = useMusicStore();    
    const [ thumb, setThumb ] = useState<FileResponse>();
    const [volume, setVolume] = useLocalStorage("volume", 0.5);
    const playerRef = useRef<AudioPlayer | null>(null);
    const pendingSeekSecondsRef = useRef<number | null>(null);
    const hasAppliedSeekRef = useRef(false);
    const lyricsModal = useLyricsModal();
    
    useEffect(() => {
        if (playlist !== undefined) {
            const item = playlist[trackIndex - 1];
            const sendNowPlaying = async () => {
                await postNowPlaying(getMusicToken(), item);
            }
            
            setCurrentTrack(item);
            sendNowPlaying();
        }
    }, [trackIndex, setCurrentTrack, playlist, getMusicToken, isAuthenticated]);

    useEffect(() => {
        const currentItem = playlist[trackIndex - 1];
        const seekSeconds = currentItem?.progressSeconds;
        pendingSeekSecondsRef.current =
            typeof seekSeconds === 'number' && Number.isFinite(seekSeconds) && seekSeconds > 0
                ? seekSeconds
                : null;
        hasAppliedSeekRef.current = false;
    }, [playlist, trackIndex]);
    
    useEffect(() => {
        if (currentTrack?.artist !== undefined && currentTrack?.album !== undefined) {    
            if (currentTrack?.letter === "Compilations") {
                callApi<FileResponse>(getCompilationThumbnailWithToken(getMusicToken().substring(0, 10), currentTrack.artist, currentTrack.album), setThumb);
            } else {
                callApi<FileResponse>(getAlbumThumbnailWithToken(getMusicToken().substring(0, 10), currentTrack.artist, currentTrack.album), setThumb);
            }
        }
    }, [currentTrack, getMusicToken])
    
    useEffect(() => {
        if (playlist.length > 0) {
            setTrackIndex(1);
        }
    }, [playlist, setTrackIndex]);
    
    const handleClickNext = () => {
        if (playMode === PlayModes.Similar) {
            setArtistName(currentTrack?.artist ?? '');
        }

        const nextTrack = trackIndex < playlist.length ? trackIndex + 1 : 0;
        setTrackIndex(nextTrack);
        if (nextTrack === 0) {
            setIsPlaying(false);
        }
    };

    const handleEnded = () => {
        if (currentTrack !== undefined) {
            const sendScrobble = async () => {
                await postScrobble(getMusicToken(), currentTrack);
                if (currentTrack?.artist !== undefined && currentTrack?.album !== undefined && currentTrack?.name !== undefined) {
                    const trackNumber = String(currentTrack.trackNumber ?? '').padStart(2, '0');
                    if (currentTrack?.scrobbleArtist === format(currentTrack.artist)) {
                        await callApi(postIncrementTrackListenCount(getAccessToken(), currentTrack.artist ?? '', currentTrack.album ?? '', `${trackNumber} - ${currentTrack.name ?? ''}`), () => {});
                    } else {
                        await callApi(postCompilationIncrementTrackListenCount(getAccessToken(), currentTrack.artist ?? '', currentTrack.album ?? '', `${trackNumber} - ${currentTrack.name ?? ''}`), () => {});
                    }
                }
            }
            
            sendScrobble();
        }

        handleClickNext();
    }

    const handleVolumeChange = (event: Event) => {
        // The current volume is stored on the underlying HTML audio element target
        const currentVolume = (event.target as HTMLAudioElement).volume;
        setVolume(currentVolume);
    };
    
    const handleLyricsClicked = () => {
        if (currentTrack !== undefined) {
            lyricsModal.setArtist(format(currentTrack.artist));
            lyricsModal.setTrack(currentTrack.name);
            lyricsModal.setLyricsModalOpen(true);
        }
    }

    const jumpToTime = (seconds: number) => {
        const audioEl = playerRef.current?.audio.current;
        if (!audioEl) {
            return false;
        }

        if (audioEl.seekable.length === 0) {
            return false;
        }

        const maxSeekable = audioEl.seekable.end(audioEl.seekable.length - 1);
        if (!Number.isFinite(maxSeekable) || maxSeekable <= 0) {
            return false;
        }

        const targetSeconds = Number.isFinite(audioEl.duration) && audioEl.duration > 0
            ? Math.min(seconds, audioEl.duration)
            : seconds;
        const boundedTarget = Math.max(Math.min(targetSeconds, Math.max(maxSeekable - 0.25, 0)), 0);
        audioEl.currentTime = boundedTarget;

        return Math.abs(audioEl.currentTime - boundedTarget) < 0.75;
    };

    const jumpToPendingTime = () => {
        if (pendingSeekSecondsRef.current === null || hasAppliedSeekRef.current) {
            return;
        }

        const success = jumpToTime(pendingSeekSecondsRef.current);
        if (success) {
            hasAppliedSeekRef.current = true;
            pendingSeekSecondsRef.current = null;
        }
    };

    return (
        <>
            <LyricsModal />
            <Stack spacing={1}>
                <Box sx={{ width: '100%' }}>
                    {trackIndex > 0 && playlist.length > 0 &&
                        <Stack direction="row" spacing={2} sx={{ width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Stack direction="row" spacing={2} sx={{ minWidth: 0 }}>
                                <Box>&nbsp; Now Playing :</Box>
                                {currentTrack?.letter === "Compilations"
                                    ?
                                    <Box>
                                        <b><Link to={`/compilations/${currentTrack?.artist}/albums/${currentTrack?.album}`}>{currentTrack?.name}</Link></b>
                                        &nbsp;on&nbsp;
                                        <b><Link to={`/compilations/${currentTrack?.artist}`}>{currentTrack?.artist}</Link></b>
                                    </Box>
                                    :
                                    <Box>
                                        <b><Link to={`/letters/${currentTrack?.letter}/artists/${currentTrack?.artist}/albums/${currentTrack?.album}`}>{currentTrack?.name ?? ''}</Link></b>
                                        &nbsp;by&nbsp;
                                        <b><Link to={`/letters/${currentTrack?.letter}/artists/${currentTrack?.artist}`}>{format(currentTrack?.artist ?? '')}</Link></b>
                                    </Box>
                                }
                            </Stack>
                            <Box sx={{ flexShrink: 0 }}>
                                <MuiLink
                                    sx={{
                                        cursor: 'pointer',
                                        textDecoration: 'none',
                                        paddingRight: 2,
                                        fontWeight: 'bold',
                                        color: 'darkgray'
                                    }}
                                    onClick={() => handleLyricsClicked()}
                                >
                                    Lyrics
                                </MuiLink>
                            </Box>
                        </Stack>
                    }
                </Box>
                <Box>
                    <Stack direction="row">
                        <Box sx={{ width: '88px', height: '88px' }}>
                            {thumb?.fileContents !== undefined ? <img alt={currentTrack?.album} style={{ width: '88px', height: '88px'}} src={`data:image/jpeg;base64,${thumb?.fileContents}`} /> : ''}
                        </Box>
                        <Box sx={{ width: '100%' }}>
                            {trackIndex > 0 && playlist.length > 0 && trackIndex <= playlist.length &&
                                <AudioPlayer
                                    ref={playerRef}
                                    autoPlay={true}
                                    preload="auto"
                                    listenInterval={250}
                                    onPlay={() => {
                                        setIsPlaying(true);
                                        jumpToPendingTime();
                                    }}
                                    src={playlist[trackIndex - 1].src}
                                    showJumpControls={false}
                                    showSkipControls={false}
                                    onClickPrevious={() => setTrackIndex(trackIndex > 1 ? trackIndex - 1 : playlist.length)}
                                    onClickNext={handleClickNext}
                                    onEnded={handleEnded}
                                    onVolumeChange={handleVolumeChange}
                                    onLoadedMetaData={() => jumpToPendingTime()}
                                    onCanPlay={() => jumpToPendingTime()}
                                    onListen={jumpToPendingTime}
                                    volume={volume}
                                />
                            }
                        </Box>
                        <Box sx={{ width: '88px', height: '88px' }}>
                            <img alt="Last FM" style={{ width: '88px', height: '88px'}} src={lastFmLogo} />
                        </Box>
                    </Stack>
                </Box>
            </Stack>
        </>
    );
}

export default MusicPlayer;